import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, signup, googleAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL parametrelerini al
  const redirectType = searchParams.get('redirect');
  const planParam = searchParams.get('plan');
  const sourceParam = searchParams.get('source');

  // Checkout'a gidiyorsa signup modunda başlat
  useEffect(() => {
    if (redirectType === 'checkout') {
      setIsSignup(true);
    }
  }, [redirectType]);

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setError('');
    setLoading(true);
    
    console.log('Google response:', credentialResponse); // Debug için
    console.log('Google response keys:', Object.keys(credentialResponse)); // Hangi field'lar var
    console.log('Google response type:', typeof credentialResponse); // Type'ı kontrol et
    
    try {
      // credentialResponse.access_token kullan
      const success = await googleAuth(credentialResponse.access_token);
      if (success) {
        // Redirect mantığı
        if (redirectType === 'checkout') {
          // Plan parametresi varsa session storage'a kaydet
          if (planParam) {
            sessionStorage.setItem('selectedPlan', planParam);
          }
          navigate(`/app/checkout?source=${sourceParam || 'landing'}`);
        } else {
          navigate('/app/dashboard');
        }
      } else {
        setError('Google authentication failed. Please try again.');
      }
    } catch (err) {
      console.error('Google auth error:', err); // Debug için
      setError('An error occurred during Google authentication');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google authentication failed. Please try again.');
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignup) {
        // Handle signup
        const success = await signup(email, password);
        if (success) {
          // Redirect mantığı
          if (redirectType === 'checkout') {
            if (planParam) {
              sessionStorage.setItem('selectedPlan', planParam);
            }
            navigate(`/app/checkout?source=${sourceParam || 'landing'}`);
          } else {
            navigate('/app/dashboard');
          }
        } else {
          setError('Signup failed. Please try again.');
        }
      } else {
        // Handle login
        const success = await login(email, password);
        if (success) {
          // Redirect mantığı
          if (redirectType === 'checkout') {
            if (planParam) {
              sessionStorage.setItem('selectedPlan', planParam);
            }
            navigate('/app/checkout');
          } else {
            navigate('/app/dashboard');
          }
        } else {
          setError('Invalid email or password');
        }
      }
    } catch (err) {
      setError(isSignup ? 'An error occurred during signup' : 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignup(!isSignup);
    setError('');
    setEmail('');
    setPassword('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(0deg, #f1fff9, #f1fff900 30%), radial-gradient(circle at 0 0, #5fd5b6 0, #5fd5b600 30%), linear-gradient(180deg, #79dfffb3 -10%, #79dfff00 70%), radial-gradient(circle at 95% 0, #d0f224 0, #5fd5b600 20%)',
        position: 'relative',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 6,
          width: '100%',
          maxWidth: 500,
          textAlign: 'center',
          position: 'relative',
          zIndex: 2,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(95, 213, 182, 0.2)',
          boxShadow: '0 8px 32px rgba(95, 213, 182, 0.15)',
          borderRadius: 3,
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Logo height={64} />
        </Box>



        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem',
              }
            }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete={isSignup ? "new-password" : "current-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem',
              }
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ 
              mt: 3, 
              mb: 2,
              py: 1.2,
              fontSize: '1rem',
              borderRadius: 2,
              fontWeight: 500,
              background: '#5fd5b6',
              '&:hover': {
                background: '#4bc4a3',
              },
              '&:disabled': {
                background: '#5fd5b6',
                color: '#fff',
                opacity: 0.7,
              }
            }}
            disabled={loading || !email || !password}
          >
            {loading ? <CircularProgress size={24} /> : (isSignup ? 'Create Account' : 'Sign In')}
          </Button>

         

          <Box sx={{ mb: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => googleLogin()}
              disabled={loading}
              startIcon={
                <Box
                  component="img"
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google"
                  sx={{ width: 18, height: 18 }}
                />
              }
              sx={{
                py: 1.2,
                fontSize: '1rem',
                borderRadius: 2,
                fontWeight: 500,
                borderColor: '#ddd',
                color: '#555',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#ccc',
                  background: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              Continue with Google
            </Button>
          </Box>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          <Button
            fullWidth
            variant="text"
            onClick={toggleMode}
            disabled={loading}
            sx={{ 
              textTransform: 'none',
              fontSize: '0.9rem',
              color: '#5fd5b6',
              '&:hover': {
                background: 'rgba(95, 213, 182, 0.1)',
              }
            }}
          >
            {isSignup 
              ? 'Already have an account? Sign In' 
              : "Don't have an account? Sign Up"
            }
          </Button>
        </Box>
        
        {/* Copyright */}
        <Box sx={{ 
          position: 'fixed', 
          bottom: 20, 
          left: 0, 
          right: 0, 
          textAlign: 'center',
          zIndex: 1000
        }}>
          <Typography 
            variant="body2" 
            color="rgba(128, 128, 128, 0.8)" 
            sx={{ 
              fontWeight: 300,
              fontSize: '0.75rem',
              letterSpacing: '0.3px'
            }}
          >
            © 2025 NowUp. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login; 